import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import AddToCalendar from 'hb-react/frontoffice/components/AddToCalendar';
import { AttendeesRow } from 'hb-react/shared/apps/Events/components/AttendeesRow';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import useEventDisplayTimeZone from 'hb-react/shared/components/EventDate/hooks/useEventDisplayTimeZone';
import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';
import TwoDigitsCalendar from 'hb-react/shared/icons/dynamic/TwoDigitsCalendar';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

import { EventWebinarUrl } from '../EventWebinarUrl';

const EventSummary = (props) => {
  const breakpoint = useBreakpoint();
  const { event } = useEventContext();
  const {
    venue,
    start_date: startDate,
    end_date: endDate,
    title,
    locations,
    show_webinar_url: showWebinarUrl,
    webinar_url: webinarUrl,
    timezone: eventTimeZone,
    display_add_to_calendar: displayAddToCalendar,
  } = event;
  const eventDisplayTimeZone = useEventDisplayTimeZone(eventTimeZone);

  const day = dayjs(startDate).tz(eventDisplayTimeZone).date();

  const address = locations?.address ?? '';

  const dateStr = getDateStr({
    startDate,
    endDate,
    timezone: eventDisplayTimeZone,
  });

  return (
    <Box flexDirection="column" {...props}>
      <ContentRow mt={0} alignItems="center" color="primary">
        <StyledTwoDigitsCalendar
          day={day}
          width={42}
          height={42}
          fontWeight={1}
        />
        <EventTitle as="h1" data-testid="event-title">
          <ClampedText clamp={2}>{title}</ClampedText>
        </EventTitle>
      </ContentRow>
      <ContentRow>
        <ClampedText clamp={2}>
          <StyledLabel title={__('Date:')} />
          {dateStr}
        </ClampedText>
      </ContentRow>
      {venue && (
        <ContentRow>
          <ClampedText clamp={['s', 'xl'].includes(breakpoint) ? 2 : 1}>
            <StyledLabel title={__('Venue:')} />
            {venue}
          </ClampedText>
        </ContentRow>
      )}
      {address && (
        <ContentRow>
          <StyledLabel title={__('Location:')} />
          <ClampedText clamp={1}>{address}</ClampedText>
        </ContentRow>
      )}
      {webinarUrl && (
        <ContentRow>
          <StyledLabel
            title={`${__('Online event')}${showWebinarUrl ? ':' : ''}`}
          />
          <EventWebinarUrl
            webinar_url={webinarUrl}
            show_webinar_url={showWebinarUrl}
            ml={1}
          />
        </ContentRow>
      )}
      {displayAddToCalendar && (
        <ContentRow>
          <AddToCalendar
            id={event.id}
            timezone={eventTimeZone}
            eventType={event.type}
          />
        </ContentRow>
      )}

      <Box mt="auto">
        <AttendeesRow event={event} />
      </Box>
    </Box>
  );
};

EventSummary.propTypes = {
  event: PropTypes.object,
};

export default EventSummary;

// COMPONENTS
const Label = ({ title, className }) => (
  <Box font="body-semibold-asphalt" className={className}>
    {title}&nbsp;
  </Box>
);

const StyledDotdotdot = styled(Dotdotdot)`
  word-break: break-word;
`;

const ClampedText = ({ children, clamp, title, className }) => (
  <StyledDotdotdot clamp={clamp} className={className}>
    <span title={title ?? children}>{children}</span>
  </StyledDotdotdot>
);

// STYLED COMPONENTS
const ContentRow = styled(Box).attrs(({ mt, alignItems }) => ({
  mt: mt ?? 3,
  alignItems: alignItems ?? 'center',
}))``;

const StyledLabel = styled(Label)`
  white-space: nowrap;
  display: inline-block;
`;

const StyledTwoDigitsCalendar = styled(TwoDigitsCalendar)`
  box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const EventTitle = styled(Box).attrs(() => ({
  pl: 3,
  font: { _: 'h4-semibold', l: 'h3-semibold' },
}))`
  /* Required so EventSummary fits within the allowed 320px in L size in a worst case scenario where all fields are displayed and at maximum content. */
  line-height: 23px !important;
  max-width: calc(100% - 42px);
`;
